import { type WithIsVisibleHOCProps } from "@client/hocs/withIsVisible";
import { connectCustom } from "@client/lib/misc";
import {
  getInheritedTextSettingSelector,
  getLayoutSettingValueSelector,
  screenSelector,
  TargetSelector
} from "@client/selectors";
import { type AppState, ScreenType } from "@client/types";
import { onePageTypeSelector } from "@client/widgets/DotsMenu/selectors";
import {
  isMenuOpenSelector,
  menuEffectSelector,
  menuPositionSelector,
  menuVisibilitySelector
} from "@client/widgets/Header/selectors";
import Menu, { INVISIBLE_PROPS, type MenuProps } from "@client/widgets/Menu/component";
import classnames from "classnames";
import {
  activeItemStyleSelector,
  enhancedMenuItemsSelector,
  itemHoverStyleSelector,
  itemStyleSelector,
  menuOpenClickHoverSelector,
  menuShowSubTitlesSelector,
  subMenuItemHoverStyleSelector,
  subMenuItemStyleSelector,
  subMenuStyleSelector
} from "./selectors";
import "./style.less";

export interface MenuContainerProps {
  isDesktop: boolean;
}

export const makeMapStateToProps = () => {
  const itemTextSelector = getInheritedTextSettingSelector(TargetSelector.Layout, "header.menu.item");
  const itemRadiusSelector = getLayoutSettingValueSelector("radius", "header.menu.item.border");
  const subItemTextSelector = getInheritedTextSettingSelector(TargetSelector.Layout, "header.submenu.item");
  const subItemRadiusSelector = getLayoutSettingValueSelector("radius", "header.submenu.item.border");

  return (state: AppState, props: MenuContainerProps): MenuProps & WithIsVisibleHOCProps => {
    const visibility = menuVisibilitySelector(state);
    if (!visibility.isVisible) return INVISIBLE_PROPS;

    const screen = screenSelector(state);
    if (screen === ScreenType.Desktop && !props.isDesktop) return INVISIBLE_PROPS;
    if (screen !== ScreenType.Desktop && props.isDesktop) return INVISIBLE_PROPS;

    const isMenuOpen = isMenuOpenSelector(state);
    const menuOpenClickHover = menuOpenClickHoverSelector(state);
    const menuPosition = menuPositionSelector(state);
    const menuShowSubTitles = menuShowSubTitlesSelector(state);
    const onePageType = onePageTypeSelector(state);
    const showMenuEffect = menuEffectSelector(state);
    return {
      id: "mainMenu",
      items: enhancedMenuItemsSelector(state),
      isVisible: true,
      isClickable: menuOpenClickHover === "menu-onclick" || screen !== ScreenType.Desktop,
      className: classnames(menuPosition, menuOpenClickHover, showMenuEffect, onePageType, {
        open: isMenuOpen,
        "menu-sub-title": menuShowSubTitles,
        vertical: screen !== ScreenType.Desktop
      }),
      activeItemStyle: activeItemStyleSelector(state),
      autoExpandCurrent: false,
      hideSubmenuOnOutsideClick: screen === ScreenType.Desktop,
      itemBackgroundPrefix: "header.menu.item",
      itemStyle: itemStyleSelector(state),
      itemRadius: itemRadiusSelector(state),
      itemHoverStyle: itemHoverStyleSelector(state),
      itemText: itemTextSelector(state),
      subMenuBackgroundPrefix: "header.submenu",
      subMenuStyle: subMenuStyleSelector(state),
      subMenuItemStyle: subMenuItemStyleSelector(state),
      subMenuItemHoverStyle: subMenuItemHoverStyleSelector(state),
      subItemRadius: subItemRadiusSelector(state),
      subItemText: subItemTextSelector(state),
      showTitles: menuShowSubTitles
    };
  };
};

export default connectCustom(makeMapStateToProps)(Menu);
